// built-from: /contextual-facilitator-companion v2 impl:contextual-facilitator-companion role:implementation
// built-from: /engagement-context-panel v1 impl:engagement-context-panel role:implementation
import { createContext, useContext, useEffect, useId, useState, type ReactNode } from 'react';
import { Maximize2, MessageSquare, Minimize2, PanelRightClose, Paperclip, X } from 'lucide-react';
import { EngagementContextPanel } from './EngagementContextPanel';
import { useFacilitatorScope } from '../hooks/useFacilitatorScope';
import '../styles/FacilitatorDock.css';

/** The one dock, optionally driven from outside. `ActivityShell` supplies this
 * so an activity's chat presentation (open, collapsed, expanded, closed) is
 * decided in one place; a host that supplies nothing keeps the dock's own
 * collapse state exactly as before. There is no second dock. */
export interface FacilitatorDockControl {
  collapsed: boolean;
  expanded: boolean;
  setCollapsed: (collapsed: boolean) => void;
  setExpanded: (expanded: boolean) => void;
  close: () => void;
}

const DockControlContext = createContext<FacilitatorDockControl | null>(null);
export const FacilitatorDockControlProvider = DockControlContext.Provider;
export const useFacilitatorDockControl = () => useContext(DockControlContext);

export function FacilitatorDock({ children, label = 'ArcPilot facilitator', className = '', engagementId, activityId }: {
  children: ReactNode; label?: string; className?: string; engagementId?: string | null; activityId?: string;
}) {
  const scope = useFacilitatorScope();
  const control = useFacilitatorDockControl();
  const [ownCollapsed, setOwnCollapsed] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const contentId = useId();
  const collapsed = control ? control.collapsed : ownCollapsed;
  const setCollapsed = control ? control.setCollapsed : setOwnCollapsed;
  const expanded = control ? control.expanded : false;
  // An explicit open request reveals the conversation again. Under a control
  // the shell owns that decision, so the dock does not overrule it.
  useEffect(() => { if (!control) setOwnCollapsed(false); }, [control, scope.openRequest]);
  const id = engagementId === undefined ? scope.engagementId : engagementId;
  return <aside className={`facilitator-dock ${collapsed ? 'facilitator-dock--collapsed' : ''} ${expanded ? 'facilitator-dock--expanded' : ''} ${className}`} aria-label={label}>
    <header className="facilitator-dock__header">
      {!collapsed ? <><div><strong>ArcPilot facilitator</strong><span>{label}</span></div>
        {id ? <button type="button" aria-expanded={contextOpen} onClick={() => setContextOpen(value => !value)}><Paperclip size={15} aria-hidden="true" />Context</button> : null}
        {control ? <button
          type="button"
          aria-label={expanded ? 'Show the activity' : 'Fill the screen with the conversation'}
          title={expanded ? 'Show the activity' : 'Fill the screen with the conversation'}
          onClick={() => control.setExpanded(!expanded)}
        >{expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}</button> : null}</> : null}
      <button
        type="button"
        aria-label={collapsed ? 'Expand facilitator' : 'Collapse facilitator'}
        title={collapsed ? 'Expand facilitator' : 'Collapse facilitator'}
        aria-expanded={!collapsed}
        aria-controls={contentId}
        onClick={() => setCollapsed(!collapsed)}
      >
        {collapsed ? <MessageSquare size={18} /> : <PanelRightClose size={18} />}
      </button>
      {control && !collapsed ? <button
        type="button"
        aria-label="Close facilitator"
        title="Close facilitator"
        onClick={control.close}
      ><X size={16} /></button> : null}
    </header>
    <div id={contentId} className="facilitator-dock__content" hidden={collapsed}>
      {contextOpen && id ? <EngagementContextPanel key={`${id}/${activityId || scope.activityId || ''}`} engagementId={id} activityId={activityId || scope.activityId} /> : null}
      {children}
    </div>
  </aside>;
}

export function FacilitatorLauncher({ opening, error, onOpen, children }: {
  opening: boolean; error?: string; onOpen: () => void; children?: ReactNode;
}) {
  return <div className="facilitator-launcher">
    {children}
    <button type="button" className="journey-button journey-button--primary" disabled={opening} onClick={onOpen}>{opening ? 'Opening facilitator…' : 'Open facilitator'}</button>
    {error ? <p role="alert">{error}</p> : null}
  </div>;
}
